'use client';
import React from 'react';
import type { ForgeArenaData } from '@/lib/services/forgeArenaService';

interface ComplianceGridProps {
  arenaData: ForgeArenaData | null;
  loading: boolean;
}

export default function ComplianceGrid({ arenaData, loading }: ComplianceGridProps) {
  const theo = arenaData?.theoCompliance7d;
  const tracy = arenaData?.tracyCompliance7d;

  const categories = [
    {
      id: 'cat-calories',
      label: 'Calorie Compliance',
      theoVal: theo?.calorieCompliance ?? 0,
      tracyVal: tracy?.calorieCompliance ?? 0,
      unit: '%',
    },
    {
      id: 'cat-protein',
      label: 'Protein Compliance',
      theoVal: theo?.proteinCompliance ?? 0,
      tracyVal: tracy?.proteinCompliance ?? 0,
      unit: '%',
    },
    {
      id: 'cat-training',
      label: 'Training Compliance',
      theoVal: theo?.trainingCompliance ?? 0,
      tracyVal: tracy?.trainingCompliance ?? 0,
      unit: '%',
    },
    {
      id: 'cat-sleep',
      label: 'Sleep Compliance',
      theoVal: theo?.sleepCompliance ?? 0,
      tracyVal: tracy?.sleepCompliance ?? 0,
      unit: '%',
    },
    {
      id: 'cat-clean',
      label: 'Clean Eating Days',
      theoVal: theo?.cleanEatingDays ?? 0,
      tracyVal: tracy?.cleanEatingDays ?? 0,
      unit: `/${theo?.totalDays ?? 7}`,
    },
  ];

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-forge-label">Compliance Breakdown</div>
        <div className="text-2xs text-muted-foreground">Last 7 Days</div>
      </div>
      {loading ? (
        <div className="space-y-4 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 bg-muted rounded w-32" />
              <div className="flex gap-1 h-2">
                <div className="flex-1 bg-muted rounded-full" />
                <div className="flex-1 bg-muted rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((cat) => {
            const theoWins = cat.theoVal >= cat.tracyVal;
            const maxVal = cat.unit === '%' ? 100 : (theo?.totalDays ?? 7);
            return (
              <div key={cat.id}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{cat.label}</span>
                  <div className="flex items-center gap-3 text-xs font-tabular">
                    <span className={`font-bold ${theoWins ? 'text-primary' : 'text-foreground'}`}>
                      {cat.theoVal}{cat.unit}
                    </span>
                    <span className="text-muted-foreground">vs</span>
                    <span className={`font-bold ${!theoWins ? 'text-accent' : 'text-foreground'}`}>
                      {cat.tracyVal}{cat.unit}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 h-2">
                  <div className="flex-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min((cat.theoVal / maxVal) * 100, 100)}%`, backgroundColor: 'var(--primary)' }}
                    />
                  </div>
                  <div className="flex-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min((cat.tracyVal / maxVal) * 100, 100)}%`, backgroundColor: 'var(--accent)' }}
                    />
                  </div>
                </div>
                <div className="flex justify-between mt-0.5">
                  <span className="text-2xs text-muted-foreground">THEO</span>
                  <span className="text-2xs text-muted-foreground">TRACY</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}